import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'

import { useGetWhatsAppBindingsQuery, useRevokeWhatsAppBindingMutation } from '@/core/services/whatsapp'

function maskPhoneNumber(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, '')
  if (digits.length <= 4) return `+${digits}`
  const keepEnd = 4
  const keepStart = Math.min(3, digits.length - keepEnd)
  const head = digits.slice(0, keepStart)
  const tail = digits.slice(-keepEnd)
  const middleLen = digits.length - keepStart - keepEnd
  return `+${head} ${'•'.repeat(Math.max(0, middleLen))} ${tail}`
}

function formatDate(dateString?: string): string {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString()
}

export default function WhatsAppBindingsPanel() {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { data, isLoading, isError } = useGetWhatsAppBindingsQuery()
  const [revokeBinding, { isLoading: isRevoking }] = useRevokeWhatsAppBindingMutation()
  const [confirmPhone, setConfirmPhone] = useState<string | null>(null)

  const handleRevoke = async () => {
    if (!confirmPhone) return
    try {
      await revokeBinding(confirmPhone).unwrap()
      enqueueSnackbar(
        t('page.configuration.whatsapp.revoke.success', { defaultValue: 'WhatsApp access revoked' }),
        { variant: 'success' }
      )
      setConfirmPhone(null)
    } catch {
      enqueueSnackbar(
        t('page.configuration.whatsapp.revoke.error', { defaultValue: 'Could not revoke WhatsApp access' }),
        { variant: 'error' }
      )
    }
  }

  const bindings = data?.bindings ?? []
  const businessPhoneNumber = data?.businessPhoneNumber ?? null

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <WhatsAppIcon sx={{ color: '#25D366' }} />
        <Typography variant="h6">
          {t('page.configuration.whatsapp.title', { defaultValue: 'WhatsApp' })}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {businessPhoneNumber
          ? t('page.configuration.whatsapp.subtitleWithNumber', {
              defaultValue:
                'Phones linked to your account can chat with the Elara assistant on WhatsApp. To link a new phone, send a message from it to {{number}}.',
              number: businessPhoneNumber,
            })
          : t('page.configuration.whatsapp.subtitle', {
              defaultValue:
                'Phones linked to your account can chat with the Elara assistant on WhatsApp. To link a new phone, send a message from it to our WhatsApp business number.',
            })}
      </Typography>

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : isError ? (
        <Alert severity="error">
          {t('page.configuration.whatsapp.loadError', { defaultValue: 'Could not load linked phones.' })}
        </Alert>
      ) : bindings.length === 0 ? (
        <Alert severity="info" variant="outlined">
          {businessPhoneNumber
            ? t('page.configuration.whatsapp.emptyWithNumber', {
                defaultValue: 'No phone linked yet. Send a WhatsApp message to {{number}} to get started.',
                number: businessPhoneNumber,
              })
            : t('page.configuration.whatsapp.empty', {
                defaultValue: 'No phone linked yet. Send a WhatsApp message to our business number to get started.',
              })}
        </Alert>
      ) : (
        <List disablePadding>
          {bindings.map((binding) => (
            <ListItem
              key={binding.phoneNumber}
              divider
              secondaryAction={
                <Button
                  color="error"
                  size="small"
                  onClick={() => setConfirmPhone(binding.phoneNumber)}
                  disabled={isRevoking}
                >
                  {t('page.configuration.whatsapp.revoke.button', { defaultValue: 'Revoke' })}
                </Button>
              }
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
              </ListItemIcon>
              <ListItemText
                primary={maskPhoneNumber(binding.phoneNumber)}
                secondary={t('page.configuration.whatsapp.linkedAt', {
                  defaultValue: 'Linked {{date}} · Last used {{lastUsed}}',
                  date: formatDate(binding.linkedAt),
                  lastUsed: formatDate(binding.lastActivityAt),
                })}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Dialog open={confirmPhone !== null} onClose={() => setConfirmPhone(null)}>
        <DialogTitle>
          {t('page.configuration.whatsapp.revoke.confirmTitle', { defaultValue: 'Revoke WhatsApp access?' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('page.configuration.whatsapp.revoke.confirmBody', {
              defaultValue:
                'The phone {{phone}} will no longer be able to chat with Elara. To relink it, send a new message from that phone.',
              phone: confirmPhone ? maskPhoneNumber(confirmPhone) : '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPhone(null)} disabled={isRevoking}>
            {t('common:button.cancel.label', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="error" variant="contained" onClick={handleRevoke} disabled={isRevoking}>
            {t('page.configuration.whatsapp.revoke.button', { defaultValue: 'Revoke' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
